import type React from "react";
import { useState } from "react";
import { ArrowRightIcon, PlusIcon } from "@icons";
import { Link, type LinkSize } from "@design-system/Link/Link";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useAnatomyMode } from "./componentAnatomyMode";
import { LinkMatrix } from "./LinkPlaygroundMatrix";
import { BooleanControl, OptionToggle } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

type LinkMode = "button" | "anchor";

const linkSizes: readonly LinkSize[] = ["sm", "xs"];
const linkModes: readonly LinkMode[] = ["button", "anchor"];

interface LinkFeatureFlags {
    isDisabled: boolean;
    hasLeftIcon: boolean;
    hasRightIcon: boolean;
    isIconOnly: boolean;
    hasTooltip: boolean;
}

/** Mirrors the component's icon union: icon-only links must carry a label. */
type SandboxSlotProps =
    | { iconOnly: true; "aria-label": string }
    | {
          iconOnly?: false;
          leftIcon?: React.ReactNode;
          rightIcon?: React.ReactNode;
      };

/** The anatomy spec's content axis, derived from the feature toggles. */
const toContentVariantId = ({
    isIconOnly,
    hasLeftIcon,
    hasRightIcon,
}: LinkFeatureFlags) => {
    if (isIconOnly) {
        return "iconOnly";
    }
    if (hasLeftIcon && hasRightIcon) {
        return "bothIcons";
    }
    if (hasLeftIcon) {
        return "leftIcon";
    }
    if (hasRightIcon) {
        return "rightIcon";
    }
    return "text";
};

/**
 * @purpose Link sandbox: size, render mode (button or anchor), icon slots,
 * icon-only, disabled and a tooltip wrapper, plus the forced-state matrix. The
 * activation counter makes it visible that disabled links ignore click and keys.
 */
export const LinkPlayground = () => {
    const [size, setSize] = useState<LinkSize>("sm");
    const [mode, setMode] = useState<LinkMode>("button");
    const [activationCount, setActivationCount] = useState(0);
    const [features, setFeatures] = useState<LinkFeatureFlags>({
        isDisabled: false,
        hasLeftIcon: false,
        hasRightIcon: false,
        isIconOnly: false,
        hasTooltip: false,
    });
    const { isDisabled, hasLeftIcon, hasRightIcon, isIconOnly, hasTooltip } =
        features;

    const anatomy = useAnatomyMode({
        component: "link",
        variantId: toContentVariantId(features),
        sizeId: size,
        isDisabled,
    });

    const handleActivate = () => setActivationCount((count) => count + 1);

    const slotProps: SandboxSlotProps = isIconOnly
        ? { iconOnly: true, "aria-label": "Next page" }
        : {
              leftIcon: hasLeftIcon ? <PlusIcon /> : undefined,
              rightIcon: hasRightIcon ? <ArrowRightIcon /> : undefined,
          };
    const label = isIconOnly ? <ArrowRightIcon /> : "Reports";
    const commonProps = {
        size,
        disabled: isDisabled,
        "data-forced-state": anatomy.forcedState,
    };

    const sandboxLink =
        mode === "anchor" ? (
            <Link
                href="#link-playground"
                onClick={handleActivate}
                {...commonProps}
                {...slotProps}
            >
                {label}
            </Link>
        ) : (
            <Link onClick={handleActivate} {...commonProps} {...slotProps}>
                {label}
            </Link>
        );

    const summary = `${size} · ${mode}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Size</h2>
                        <OptionToggle
                            options={linkSizes}
                            value={size}
                            onChange={setSize}
                        />
                        <h2 className={styles.subheading}>Mode</h2>
                        <OptionToggle
                            options={linkModes}
                            value={mode}
                            onChange={setMode}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Disabled"
                                checked={isDisabled}
                                onChange={(nextIsDisabled) =>
                                    setFeatures({
                                        ...features,
                                        isDisabled: nextIsDisabled,
                                    })
                                }
                            />
                            <BooleanControl
                                label="Left icon"
                                checked={hasLeftIcon}
                                disabled={isIconOnly}
                                onChange={(nextHasLeftIcon) =>
                                    setFeatures({
                                        ...features,
                                        hasLeftIcon: nextHasLeftIcon,
                                    })
                                }
                            />
                            <BooleanControl
                                label="Right icon"
                                checked={hasRightIcon}
                                disabled={isIconOnly}
                                onChange={(nextHasRightIcon) =>
                                    setFeatures({
                                        ...features,
                                        hasRightIcon: nextHasRightIcon,
                                    })
                                }
                            />
                            <BooleanControl
                                label="Icon only"
                                checked={isIconOnly}
                                onChange={(nextIsIconOnly) =>
                                    setFeatures({
                                        ...features,
                                        isIconOnly: nextIsIconOnly,
                                    })
                                }
                            />
                            <BooleanControl
                                label="Tooltip"
                                checked={hasTooltip}
                                onChange={(nextHasTooltip) =>
                                    setFeatures({
                                        ...features,
                                        hasTooltip: nextHasTooltip,
                                    })
                                }
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={<LinkMatrix />}
                output={JSON.stringify(
                    {
                        size,
                        mode,
                        disabled: isDisabled,
                        leftIcon: !isIconOnly && hasLeftIcon,
                        rightIcon: !isIconOnly && hasRightIcon,
                        iconOnly: isIconOnly,
                        tooltip: hasTooltip ? "Open reports" : null,
                        forcedState: anatomy.forcedState ?? null,
                        activations: activationCount,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ??
                    (hasTooltip ? (
                        <Tooltip content="Open reports">{sandboxLink}</Tooltip>
                    ) : (
                        sandboxLink
                    ))}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
